import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, clearAuthTokens, hasAuthFallback, hasStoredAuthSession, pauseAuthRefresh, refreshTokens, resumeAuthRefresh, saveAuthTokens } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warmingUp, setWarmingUp] = useState(true);
  const authVersionRef = useRef(0);

  const refresh = useCallback(async () => {
    const version = authVersionRef.current;
    if (!hasStoredAuthSession()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (version !== authVersionRef.current) return;
      setUser(data);
    } catch {
      if (version !== authVersionRef.current) return;
      if (hasAuthFallback()) {
        try {
          await refreshTokens();
          const { data } = await api.get("/auth/me");
          if (version !== authVersionRef.current) return;
          setUser(data);
          return;
        } catch {
          clearAuthTokens();
        }
      }
      setUser(false);
    } finally {
      if (version === authVersionRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Warm up backend immediately
    api.get("/health").finally(() => setWarmingUp(false));
    refresh();
  }, [refresh]);

  const login = async (emailOrPhone, password) => {
    authVersionRef.current += 1;
    resumeAuthRefresh();
    const isEmail = emailOrPhone.includes("@");
    const payload = isEmail
      ? { email: emailOrPhone, password }
      : { mobile_number: emailOrPhone, password };
    const { data } = await api.post("/auth/login", payload);
    saveAuthTokens(data?.token, data?.refresh_token);
    setUser(data?.user || false);
    setLoading(false);
    return data?.user;
  };

  const register = async (payload) => {
    authVersionRef.current += 1;
    resumeAuthRefresh();
    const { data } = await api.post("/auth/register", payload);
    saveAuthTokens(data?.token, data?.refresh_token);
    setUser(data.user);
    setLoading(false);
    return data.user;
  };

  const logout = async () => {
    authVersionRef.current += 1;
    pauseAuthRefresh();
    clearAuthTokens();
    localStorage.removeItem("redirectAfterLogin");
    setUser(false);
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, warmingUp, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
